import type { UseFormRegister, UseFormWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Badge, Switch, brandFillClass } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ACTIVITY_TYPE_CONFIG, activityTypeLabel } from "@/constants";
import { useAuthStore } from "@/store/authStore";
import { formatCommissionPercentLabel } from "@/lib/commissionRate";
import { ActivityPaymentModel, Permission, type Activity } from "@/types";
import type { ActivityDetailFormValues } from "../activityDetailForm";
import { EntityHeaderLayout, ItemThumbnail } from "@/components/common";
import { ArrowUpRight, MapPin, ShieldCheck, UserRound } from "lucide-react";
import { SUPPORTED_LOCALES } from "@/lib/locales";
import {
  resolveCatalogTranslation,
  type TranslationFormValues,
} from "@/lib/translationResolution";
import { ActivityPartnerShareBadge } from "./ActivityPartnerShareBadge";

interface ActivityHeaderProps {
  activity: Activity;
  nameTranslations: TranslationFormValues;
  register: UseFormRegister<ActivityDetailFormValues>;
  watch: UseFormWatch<ActivityDetailFormValues>;
  visibilityModified: boolean;
  /** When true, visibility toggle is disabled and forced off (incomplete legal profile). */
  activationLocked: boolean;
  /**
   * Staff editing someone else's activity: shows the platform commission and a
   * staff-mode note instead of the owner's partner share.
   */
  staffMode: boolean;
  /** Admin profile of the owner (staff only). */
  partnerPath: string | null;
}

export function ActivityHeader({
  activity,
  nameTranslations,
  register,
  watch,
  visibilityModified,
  activationLocked,
  staffMode,
  partnerPath,
}: ActivityHeaderProps) {
  const { t, i18n } = useTranslation("activities");
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const showPaymentModel =
    hasPermission(Permission.ACTIVITIES_READ_ANY) ||
    hasPermission(Permission.ACTIVITIES_MANAGE_ANY);
  const config = ACTIVITY_TYPE_CONFIG[activity.category];
  const paymentModelLabel =
    activity.payment_model === ActivityPaymentModel.RECEIPT_BASED
      ? t("header.paymentModelReceiptBased")
      : t("header.paymentModelUpfront");
  const TypeIcon = config.icon;
  const coverImage = activity.media
    ?.filter((item) => item.type === "image" && item.url)
    .sort((a, b) => a.order - b.order)[0]?.url;
  const listed = activationLocked ? false : watch("is_visible");
  const displayName =
    resolveCatalogTranslation(nameTranslations, i18n.language) ||
    SUPPORTED_LOCALES.map((code) => nameTranslations[code]?.trim()).find(
      Boolean,
    ) ||
    "";

  return (
    <EntityHeaderLayout
      modified={visibilityModified}
      className={cn(
        "animate-slide-up via-surface-0 to-surface-0 bg-gradient-to-br",
        config.color === "accent" && "from-accent-50/80",
        config.color === "primary" && "from-primary-50/80",
        config.color === "success" && "from-success-50/80",
      )}
      icon={
        coverImage ? (
          <ItemThumbnail
            imageUrl={coverImage}
            alt={displayName}
            fallbackIcon={TypeIcon}
            rounded="xl"
            className="ring-surface-200 h-14 w-14 shadow-sm ring-1"
          />
        ) : (
          <div
            className={cn(
              "flex h-14 w-14 items-center justify-center rounded-xl shadow-sm",
              brandFillClass(config.color),
            )}
          >
            <TypeIcon
              className="h-7 w-7"
              aria-hidden
            />
          </div>
        )
      }
      aside={
        <Switch
          disabled={activationLocked}
          aria-label={t("detail.visibilityToggleAria")}
          aria-disabled={activationLocked}
          {...register("is_visible")}
          label={t("detail.visibilityLabel")}
          description={
            activationLocked
              ? t("detail.visibilityLockedHint")
              : t("detail.visibilityHint")
          }
        />
      }
    >
      <h1 className="text-surface-900 text-2xl font-semibold tracking-tight text-balance sm:text-[1.75rem] sm:leading-tight">
        {displayName || t("detail.untitledActivity")}
      </h1>
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <Badge
          variant={
            config.color === "accent"
              ? "accent"
              : config.color === "primary"
                ? "primary"
                : "success"
          }
          size="sm"
        >
          {activityTypeLabel(activity.category)}
        </Badge>
        {!activity.is_active ? (
          <Badge
            variant="default"
            size="sm"
            dot
          >
            {t("card.inactive")}
          </Badge>
        ) : (
          <Badge
            variant={listed ? "success" : "default"}
            size="sm"
            dot
          >
            {listed ? t("card.active") : t("card.hidden")}
          </Badge>
        )}
        {staffMode ? null : (
          <ActivityPartnerShareBadge
            platformCommissionRate={activity.commission_rate}
          />
        )}
        {showPaymentModel ? (
          <Badge
            variant="outline"
            size="sm"
          >
            {paymentModelLabel}
          </Badge>
        ) : null}
        {staffMode ? (
          <Badge
            variant="outline"
            size="sm"
            className="tabular-nums"
          >
            {t("detail.headerCommission", {
              percent: formatCommissionPercentLabel(activity.commission_rate),
            })}
          </Badge>
        ) : null}
      </div>
      {staffMode || activity.city || activity.address || partnerPath ? (
        <div className="text-surface-500 mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px]">
          {staffMode ? (
            <span className="text-accent-700 flex min-w-0 items-start gap-1.5 font-medium">
              <ShieldCheck
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
                aria-hidden
              />
              {t("detail.adminEditingBanner")}
            </span>
          ) : null}
          {activity.city || activity.address ? (
            <span className="flex min-w-0 items-center gap-1.5">
              <MapPin
                className="text-surface-400 h-3.5 w-3.5 shrink-0"
                aria-hidden
              />
              <span className="truncate">
                {activity.city || activity.address}
              </span>
            </span>
          ) : null}
          {partnerPath ? (
            <Link
              to={partnerPath}
              className="group text-primary-700 hover:text-primary-800 inline-flex items-center gap-1.5 font-medium hover:underline"
            >
              <UserRound
                className="h-3.5 w-3.5 shrink-0"
                aria-hidden
              />
              {t("detail.goToPartner")}
              <ArrowUpRight
                className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 group-hover:translate-x-px group-hover:-translate-y-px"
                aria-hidden
              />
            </Link>
          ) : null}
        </div>
      ) : null}
    </EntityHeaderLayout>
  );
}
